export type ContinentCode = "asia" | "eu" | "na" | "sa" | "af" | "oc";

export const CONTINENT_CODES = new Set<ContinentCode>([
  "asia",
  "eu",
  "na",
  "sa",
  "af",
  "oc",
]);

const CONTINENT_COUNTRIES: Record<ContinentCode, readonly string[]> = {
  asia: [
    "af", "am", "az", "bh", "bd", "bt", "bn", "kh", "cn", "cy", "ge", "in",
    "id", "ir", "iq", "il", "jp", "jo", "kz", "kp", "kr", "kw", "kg", "la",
    "lb", "my", "mv", "mn", "mm", "np", "om", "pk", "ps", "ph", "qa", "sa",
    "sg", "lk", "sy", "tw", "tj", "th", "tl", "tr", "tm", "ae", "uz", "vn",
    "ye", "hk", "mo",
  ],
  eu: [
    "ax", "al", "ad", "at", "by", "be", "ba", "bg", "hr", "cz", "dk", "ee",
    "fo", "fi", "fr", "de", "gi", "gr", "gg", "va", "hu", "is", "ie", "im",
    "it", "je", "lv", "li", "lt", "lu", "mk", "mt", "md", "mc", "me", "nl",
    "no", "pl", "pt", "ro", "ru", "sm", "rs", "sk", "si", "es", "sj", "se",
    "ch", "ua", "gb", "xk",
  ],
  na: [
    "ca", "mx", "us", "gl", "pm", "bm", "bs", "cr", "cu", "do", "sv", "gt",
    "hn", "ht", "jm", "ni", "pa", "pr", "tt",
  ],
  sa: ["ar", "bo", "br", "cl", "co", "ec", "fk", "gf", "gy", "pe", "py", "sr", "uy", "ve"],
  af: [
    "dz", "ao", "bj", "bw", "bf", "bi", "cv", "cm", "cf", "td", "km", "cg",
    "cd", "ci", "dj", "eg", "gq", "er", "sz", "et", "ga", "gm", "gh", "gn",
    "gw", "ke", "ls", "lr", "ly", "mg", "mw", "ml", "mr", "mu", "yt", "ma",
    "mz", "na", "ne", "ng", "re", "rw", "st", "sn", "sc", "sl", "so", "za",
    "ss", "sd", "tz", "tg", "tn", "ug", "eh", "zm", "zw",
  ],
  oc: [
    "au", "nz", "fj", "pg", "sb", "vu", "nc", "pf", "ws", "to", "tv", "ki",
    "nr", "fm", "mh", "pw", "ck", "nu", "tk", "wf",
  ],
};

function buildCountryToContinentMap(): Record<string, ContinentCode> {
  const map: Record<string, ContinentCode> = {};
  (Object.entries(CONTINENT_COUNTRIES) as [ContinentCode, readonly string[]][]).forEach(
    ([continent, countries]) => {
      countries.forEach((country) => {
        map[country] = continent;
      });
    }
  );
  return map;
}

export const COUNTRY_TO_CONTINENT: Record<string, ContinentCode> =
  buildCountryToContinentMap();

/** When a continent has no endpoint, try these continents in order. */
export const NEAREST_CONTINENTS: Record<ContinentCode, ContinentCode[]> = {
  asia: ["eu", "na", "oc"],
  eu: ["asia", "na", "af"],
  na: ["eu", "sa", "asia"],
  sa: ["na", "eu"],
  af: ["eu", "asia", "na"],
  oc: ["asia", "na", "eu"],
};

export function normalizeRegionCode(raw?: string | null): string | null {
  if (!raw || typeof raw !== "string") return null;
  const code = raw.trim().toLowerCase();
  if (code.length < 2) return null;
  return code;
}

/** ISO country codes that share a slug with a continent code (e.g. na = Namibia vs na = North America). */
export function isCountryContinentSlugConflict(region: string): boolean {
  return (
    CONTINENT_CODES.has(region as ContinentCode) &&
    !!COUNTRY_TO_CONTINENT[region]
  );
}
